import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SeoHead } from '../components/SeoHead';
import { getStaticPageMetadata, BLOG_ROBOTS } from '../lib/seo';
import { PageContainer } from '../src/components/ui/page-container';
import { PageTitle } from '../src/components/ui/typography';
import { EmptyState } from '../src/components/ui/empty-state';
import { Button } from '../src/components/ui/button';
import { getPublishedPosts } from '../services/blogService';
import { toast } from '../src/lib/toast';
import type { HydratedBlogPost } from '../src/types/blog';
import { getSSGPageData } from '../lib/ssGData';
import { toSeededPosts, readTimeBySlug } from '../lib/blogSeed';
import PostCard from '../src/components/blog/PostCard';
import PostCardSkeleton from '../src/components/blog/PostCardSkeleton';
import CategoryPillNav from '../src/components/blog/CategoryPillNav';
import BlogCtaCard from '../src/components/blog/BlogCtaCard';
import BeltIcon from '../src/components/ui/BeltIcon';
import { POSTS_PER_PAGE, postsPerPageFor } from '../src/constants/blog';
import { useBreakpointUp } from '../hooks/useBreakpointUp';

const BlogIndexPage: React.FC = () => {
    // H7: first paint comes from the prerendered post list when there is one, so the
    // page a crawler reads is the page a visitor sees. isLoading starts FALSE in that
    // case — starting true would paint skeletons over content that is already in the
    // html, which is both a visible flash and a hydration mismatch.
    // Adopt the build's list ONLY when it was built for THIS route. The __SSG_DATA__ tag
    // lives outside the React root and is never rewritten on client-side navigation, so
    // after a pill click the DOM still holds the previous page's payload — seeding from it
    // rendered one route's posts on another. The gym routes guard the same hazard by
    // matching stableId (App.tsx). A rejected seed leaves seededPosts empty, which turns
    // the fetch back on, so the page self-corrects instead of showing the wrong list.
    const ssgBlogPosts = useMemo(() => {
        const d = getSSGPageData();
        return d?.blogPosts && d.blogListingCategory === null ? d.blogPosts : undefined;
    }, []);
    const seededPosts = useMemo(() => toSeededPosts(ssgBlogPosts), [ssgBlogPosts]);
    const seededReadTimes = useMemo(() => readTimeBySlug(ssgBlogPosts), [ssgBlogPosts]);
    const [posts, setPosts] = useState<HydratedBlogPost[]>(seededPosts);
    const [isLoading, setIsLoading] = useState(seededPosts.length === 0);
    const [isError, setIsError] = useState(false);
    const [visibleCount, setVisibleCount] = useState<number>(POSTS_PER_PAGE);
    // Responsive page size: 9 on desktop (lg+), 6 below (mobile/tablet).
    // Item 1: this follows the stylesheet's own `lg:` tier, asked of the browser in
    // REM, rather than comparing a pixel width to 1024 — the two disagree for a
    // visitor with a raised default font size. `useBreakpointUp` returns the
    // SSR-safe answer on the first render, so the initial slice still matches the
    // SSG HTML exactly, which is the property the floor below depends on.
    const isLgUp = useBreakpointUp('lg');
    const pageSize = postsPerPageFor(isLgUp);

    // Single source of truth for /blog metadata: SEO head + on-page descriptor.
    const meta = getStaticPageMetadata('/blog')!;

    // Re-invocable fetch — called on mount and by the error-state "Try again"
    // retry. The request-id guard ignores stale resolutions (unmount or a
    // rapid re-invoke), replacing the previous per-effect `cancelled` flag.
    // On failure: distinct error state + toast (item 25; toast via item 22).
    const requestIdRef = useRef(0);
    const loadPosts = useCallback(() => {
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        setIsError(false);
        getPublishedPosts()
            .then((result) => {
                if (requestId !== requestIdRef.current) return;
                setPosts(result);
                setIsLoading(false);
            })
            .catch(() => {
                if (requestId !== requestIdRef.current) return;
                setIsError(true);
                setIsLoading(false);
                toast.error("Couldn't load posts. Please try again.");
            });
    }, []);

    useEffect(() => {
        // H7: when the build shipped a post list, THAT is the truth and we do not fetch.
        // A post exists in Firestore before the platform publishes it — publication is the
        // DEPLOY, on a deliberate delay — so fetching here surfaced posts whose pages are
        // not built yet: a visitor could click a card and land on the not-found shell, and
        // the list visibly reshuffled a moment after load. Trusting the deployed list fixes
        // both. The fetch remains the fallback for local dev, where there is no build data.
        if (seededPosts.length > 0) return;
        loadPosts();
    }, [loadPosts, seededPosts.length]);

    // Reset the visible slice whenever the posts identity changes, or when the
    // responsive page size flips across the lg breakpoint (resize) — but NEVER below
    // what the server already rendered.
    //
    // H7 made that floor necessary. The server renders POSTS_PER_PAGE (9) because the
    // breakpoint reports its SSR-safe answer (lg+) on the first render; on a phone the
    // real answer arrives moments later, pageSize drops to 6, and without the floor this
    // effect would delete three cards the visitor is already looking at. Before H7
    // nothing was rendered until the fetch resolved, so the shrink had nothing to remove.
    const ssrRenderedCount = seededPosts.length > 0 ? Math.min(POSTS_PER_PAGE, seededPosts.length) : 0;
    useEffect(() => {
        setVisibleCount(Math.max(pageSize, ssrRenderedCount));
    }, [posts, pageSize, ssrRenderedCount]);

    const paginatedPosts = useMemo(
        () => posts.slice(0, visibleCount),
        [posts, visibleCount]
    );

    const hasMore = posts.length > paginatedPosts.length;

    const handleLoadMore = () => setVisibleCount((prev) => prev + pageSize);

    return (
        <div className="min-h-screen bg-surface-canvas">
            <SeoHead meta={{ ...meta, robots: BLOG_ROBOTS }} />
            <div className="h-16" />
            <main id="main-content" className="text-text-primary">
                <PageContainer size="content" className="py-12">
                    {/* Page header */}
                    <header className="mb-4">
                        {/* min-h-[35px] reserves the 28px-title row height so the mobile 20px title
                            occupies the SAME height as the category-page titles → no page shift when
                            switching categories via the pill nav (matches BlogCategoryPage).
                            UI-44: that reservation only ever covered the MOBILE tier. At ≥768px this
                            row rendered 42px against the category pages' 35px — see the title below
                            for the cause and the fix. */}
                        <div className="flex items-center gap-4 min-h-[35px]">
                            {/* MAN belt motif — steel blue, h-6 (≈57px wide at the 410×171 asset ratio),
                                foreground icon next to the page title. */}
                            <BeltIcon variant="blue" className="h-6 w-auto flex-shrink-0" />
                            {/* Mobile shrinks to 20px so the title fits ONE line beside the belt icon
                                (down to ~360px); tablet/desktop keep 28px.
                                UI-44 — THE SIZE AND THE LINE-HEIGHT MUST STAY WELDED (`/[1.25]`).
                                PageTitle's base is `text-[28px] … leading-[1.25]`, but tailwind-merge
                                treats font-size and leading as ONE group (Tailwind v4 lets `text-*`
                                carry a `/leading` modifier), so a bare size override here evicted the
                                BASE LEADING TOO. The h1 then inherited 1.5 and this row rendered 42px
                                against the category pages' 35px — the visible jump when switching
                                categories. The `/[1.25]` shorthand emits both values in a single rule,
                                so they cannot be separated again. Do NOT rewrite these as bare
                                `text-[20px] md:text-[28px]`. */}
                            <PageTitle className="text-brand-primary text-[20px]/[1.25] md:text-[28px]/[1.25]">Martial Arts Network Blog</PageTitle>
                        </div>
                    </header>

                    {/* Shared category nav — /blog shows the "All" pill as active. */}
                    <CategoryPillNav activeCategory="all" />

                    {/* Post grid / loading / empty — index page shows all published posts; per-category filtering is route-based via /blog/:category. */}
                    {isLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" role="status" aria-label="Loading articles" aria-busy="true">
                            {Array.from({ length: pageSize }).map((_, i) => (
                                <PostCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : isError ? (
                        <EmptyState
                            headingLevel={2}
                            title="Couldn't load posts"
                            description="Something went wrong. Please try again."
                            action={
                                <Button variant="secondary" onClick={loadPosts}>
                                    Try again
                                </Button>
                            }
                        />
                    ) : posts.length === 0 ? (
                        <EmptyState
                            headingLevel={2}
                            title="No posts yet"
                            description="Check back soon — new articles are on the way."
                        />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {paginatedPosts.map((post) => (
                                <PostCard key={post.id} post={post} readTime={seededReadTimes[post.slug]} />
                            ))}
                        </div>
                    )}

                    {/* Client-side Load More — mirrors DirectoryPage's idiom
                        (pages/DirectoryPage.tsx:564-570) with col-span-full
                        dropped because the button sits OUTSIDE the grid. */}
                    {hasMore && (
                        <div className="flex justify-center py-4">
                            <Button variant="primary" onClick={handleLoadMore} className="px-6">
                                Load More ({posts.length - paginatedPosts.length} remaining)
                            </Button>
                        </div>
                    )}

                    <BlogCtaCard className="mt-12" />
                </PageContainer>
            </main>
        </div>
    );
};

export default BlogIndexPage;
